import { useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

import { queryParametersSchema, QueryParametersSchema, get } from './../schema';
import { useStaticProps, useQueryParameter } from '../context';

type ParamKey = keyof QueryParametersSchema;

export function useSchema() {
  const { search } = useLocation();
  const staticProps = useStaticProps();
  const { queryParameters } = useQueryParameter();

  const parameters = useMemo(() => queryParametersSchema.parameters, []);
  const urlSearchParams = useMemo(() => new URLSearchParams(search), [search]);

  const getParameter = useCallback(<K extends ParamKey>(name: K) => get(name), []);
  const getDefaultValue = useCallback(
    <K extends ParamKey>(name: K) =>
      get(name)?.getDefaultValue({ initialQueryParameters: staticProps.initialQueryParameter }),
    [staticProps]
  );
  const getValue = useCallback(
    <K extends ParamKey>(name: K) => get(name)?.getValue({ staticProps, queryParameters }),
    [queryParameters, staticProps]
  );
  const convertValueToUrlQueryString = useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = get(name);
      if (!parameter || !parameter.toUrlSearchParams) return null;

      const value = getValue(name);
      const defaultValue = getDefaultValue(name);
      return parameter.toUrlSearchParams({
        defaultValue,
        value,
        staticProps,
        queryParameters,
      });
    },
    [queryParameters, staticProps, getValue, getDefaultValue]
  );
  const convertUrlQueryStringToParam = useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = get(name);
      if (!parameter || !parameter.fromUrlSearchParams) return null;

      const defaultValue = getDefaultValue(name);
      return parameter.fromUrlSearchParams({ defaultValue, staticProps, urlSearchParams });
    },
    [staticProps, urlSearchParams, getDefaultValue]
  );
  const convertValueToSearchQuery = useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = get(name);
      if (!parameter || !parameter.toSearchQuery) return null;

      const value = getValue(name);
      const defaultValue = getDefaultValue(name);
      return parameter.toSearchQuery({
        defaultValue,
        value,
        staticProps,
        queryParameters,
      });
    },
    [staticProps, queryParameters, getValue, getDefaultValue]
  );
  const sanitizeQueryParameter = useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = get(name);
      if (!parameter || !parameter.sanitize) return null;

      const value = getValue(name);
      const defaultValue = getDefaultValue(name);
      return parameter.sanitize({
        defaultValue,
        value,
        staticProps,
        queryParameters,
      });
    },
    [staticProps, queryParameters, getValue, getDefaultValue]
  );

  return {
    parameters,
    getValue,
    getParameter,
    getDefaultValue,
    convertValueToUrlQueryString,
    convertUrlQueryStringToParam,
    convertValueToSearchQuery,
    sanitizeQueryParameter,
  };
}
